import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Sparkles, CheckCircle2, Phone, MessageCircle, ArrowRight, X,
} from 'lucide-react';
import { PRODUCTS, BRAND } from '../data/mock';
import { Button } from '../components/ui/button';
import { useQuote } from '../context/QuoteContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';
import EnquiryModal from '../components/EnquiryModal';
import SectionHeading from '../components/SectionHeading';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absoluteUrl = (u) => (u.startsWith('http') ? u : `${BACKEND}${u}`);

const ProductPage = () => {
  const { slug } = useParams();
  const { openQuote } = useQuote();
  const product = useMemo(() => PRODUCTS.find((p) => p.slug === slug), [slug]);
  const [lightbox, setLightbox] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    if (!product) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/products/${product.slug}/images`);
        if (!cancelled) setUploadedImages(data.map((d) => absoluteUrl(d.url)));
      } catch {
        if (!cancelled) setUploadedImages([]);
      }
    })();
    return () => { cancelled = true; };
  }, [product]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => e.key === 'Escape' && setLightbox(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  if (!product) return <Navigate to="/" replace />;

  const gallery = uploadedImages.length > 0 ? uploadedImages : product.gallery;
  const heroSrc = uploadedImages[0] || product.hero;
  const others = PRODUCTS.filter((p) => p.slug !== product.slug).slice(0, 3);

  return (
    <div className="bg-white min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 pt-28 md:pt-32" data-testid={`product-page-${product.slug}`}>
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-sm text-stone-500 flex items-center gap-2">
            <Link to="/" className="hover:text-pink-brand transition-colors">Home</Link>
            <ArrowRight className="w-3 h-3" />
            <Link to="/products" className="hover:text-pink-brand transition-colors">Products</Link>
            <ArrowRight className="w-3 h-3" />
            <span className="text-stone-800 font-medium">{product.name}</span>
          </div>
        </div>

        {/* Hero */}
        <section className="section-pad">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-10 items-center">
            <div className="order-2 md:order-1">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-8 bg-pink-brand/60" />
                <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.28em] text-pink-brand">
                  Signature Product
                </span>
              </div>
              <h1 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl text-stone-900 leading-[1.05]">
                {product.name.split(' ').map((w, i, arr) =>
                  i === arr.length - 1
                    ? <em key={i} className="font-script not-italic text-pink-brand font-semibold">{w}</em>
                    : <span key={i}>{w} </span>
                )}
              </h1>
              <p className="mt-3 font-script text-pink-brand text-xl">{product.tagline}</p>
              <p className="mt-5 text-stone-600 leading-relaxed text-lg">{product.short}</p>
              <p className="mt-4 text-stone-600 leading-relaxed">{product.long}</p>

              <ul className="mt-6 grid grid-cols-2 gap-3">
                {product.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-stone-700">
                    <CheckCircle2 className="w-4 h-4 text-pink-brand mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button
                  onClick={() => openQuote({ event_type: product.name, source: `product-${product.slug}-hero` })}
                  className="bg-pink-brand hover:bg-[#BE185D] text-white rounded-full h-12 px-8 font-semibold shadow-pink-glow"
                  data-testid={`product-hero-quote-${product.slug}`}
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Request a Quote
                </Button>
                <a
                  href={`https://wa.me/${BRAND.phoneRaw}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full h-12 px-6 bg-white text-stone-900 border border-pink-200 hover:border-pink-brand font-semibold text-sm"
                >
                  <MessageCircle className="w-4 h-4 text-pink-brand" /> WhatsApp
                </a>
                <a
                  href={`tel:${BRAND.phone}`}
                  className="inline-flex items-center gap-2 rounded-full h-12 px-6 text-pink-brand font-semibold hover:bg-blush/40"
                >
                  <Phone className="w-4 h-4" /> {BRAND.phone}
                </a>
              </div>
            </div>

            <div className="order-1 md:order-2 relative">
              <div className="relative rounded-[2.5rem] overflow-hidden shadow-floating border border-pink-100 aspect-[4/5] bg-blush">
                <img src={heroSrc} alt={product.name} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-black/10 to-transparent" />
                <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white/95 text-pink-brand text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-sm">
                  <Sparkles className="w-3 h-3" /> Mr. Coconetto Original
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery */}
        <section className="section-pad bg-cream">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <SectionHeading
              eyebrow="Our Work"
              title="From Real"
              accent="Events"
              subtitle={`A peek into recent ${product.name.toLowerCase()} we've produced across India.`}
            />

            <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6" data-testid={`product-gallery-${product.slug}`}>
              {gallery.map((src, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setLightbox(src)}
                  className="group relative overflow-hidden rounded-2xl aspect-square bg-blush focus:outline-none focus:ring-2 focus:ring-pink-brand"
                  data-testid={`product-gallery-item-${product.slug}-${i}`}
                >
                  <img
                    src={src}
                    alt={`${product.name} ${i + 1}`}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Related products */}
        <section className="section-pad bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <SectionHeading
              eyebrow="Explore More"
              title="Other"
              accent="Signature"
              tail="Products"
            />

            <div className="mt-14 grid sm:grid-cols-3 gap-6">
              {others.map((op) => (
                <Link
                  key={op.slug}
                  to={`/products/${op.slug}`}
                  className="coco-card group relative overflow-hidden rounded-3xl bg-white border border-pink-100 shadow-soft"
                  data-testid={`product-related-${op.slug}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-blush">
                    <img src={op.hero} alt={op.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-semibold text-lg text-stone-900">{op.name}</h3>
                    <p className="text-sm text-stone-500 mt-1 italic line-clamp-1">{op.tagline}</p>
                    <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-pink-brand">
                      Explore <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-12 text-center">
              <Link
                to="/products"
                className="inline-flex items-center gap-1.5 text-pink-brand font-semibold hover:gap-2 transition-all"
                data-testid="product-back-to-all"
              >
                <ArrowLeft className="w-4 h-4" /> Back to all products
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingButtons />
      <EnquiryModal />

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
          onClick={() => setLightbox(null)}
          data-testid="product-gallery-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
            aria-label="Close"
            data-testid="product-gallery-lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={lightbox}
            alt="Preview"
            className="max-w-full max-h-full rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default ProductPage;
